import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Check, Copy, Download, Terminal, X } from "lucide-react";
import Reveal from "@/components/Reveal";
import EmberCanvas from "@/components/EmberCanvas";
import { EMBLEM_URL } from "@/data/content";
import { GAMES, RELEASES } from "@/data/releases";

const CopyButton = ({ text, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <button
      onClick={copy}
      data-testid={testid}
      className="group/copy flex items-center gap-1.5 border border-[#282D37] px-2.5 py-1.5 font-mono text-[9px] tracking-[0.2em] text-[#9EA4B0] transition-colors duration-300 hover:border-[#E52E2E]/60 hover:text-[#E52E2E]"
      aria-label={`Copy ${text}`}
    >
      {copied ? <Check size={11} className="text-[#E52E2E]" /> : <Copy size={11} />}
      {copied ? "COPIED" : "COPY"}
    </button>
  );
};

const AddonDetail = () => {
  const { slug } = useParams();
  const [lightbox, setLightbox] = useState(null);
  const release = RELEASES.find((r) => r.slug === slug);

  if (!release) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center px-6 pt-32 text-center">
        <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">SIGNAL LOST</p>
        <h1 className="mt-4 font-display text-4xl font-black uppercase text-[#F4F5F7]">Project not found</h1>
        <Link
          to="/projects"
          data-testid="addon-notfound-back"
          className="mt-8 border border-[#3A404E] px-8 py-4 font-mono text-xs font-bold tracking-[0.25em] text-[#F4F5F7] transition-colors duration-300 hover:border-[#E52E2E] hover:text-[#E52E2E]"
        >
          ALL PROJECTS
        </Link>
      </div>
    );
  }

  const game = GAMES[release.game];

  return (
    <div className="relative pt-32">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[520px]"
        style={{
          background: "radial-gradient(ellipse 60% 100% at 50% 0%, rgba(229,46,46,0.08) 0%, transparent 70%)",
        }}
      />
      <header className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <Reveal>
          <Link
            to="/projects"
            data-testid="addon-back-link"
            className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] text-[#9EA4B0] transition-colors duration-300 hover:text-[#E52E2E]"
          >
            <ArrowLeft size={14} /> ALL PROJECTS
          </Link>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <img src={EMBLEM_URL} alt="Arsenal Forge emblem" className="emblem-glow w-20 sm:w-24" />
            <div>
              <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">
                {game.name.toUpperCase()} · {release.client.toUpperCase()}
              </p>
              <h1 className="mt-2 font-display text-5xl font-black uppercase leading-none tracking-tight text-[#F4F5F7] sm:text-7xl">
                {release.displayName}
              </h1>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-2 font-mono text-[10px] font-bold tracking-[0.25em]">
            <span className="border border-[#E52E2E]/60 bg-[#E52E2E]/10 px-3 py-1.5 text-[#E52E2E]" data-testid="badge-beta">{release.status}</span>
            <span className="border border-[#282D37] px-3 py-1.5 text-[#F4F5F7]">v{release.version}</span>
            <span className="border border-[#282D37] px-3 py-1.5 text-[#F4F5F7]">{release.pricing}</span>
            <span className="border border-[#282D37] px-3 py-1.5 text-[#9EA4B0]">{release.category.toUpperCase()}</span>
          </div>
          <p className="mt-8 max-w-2xl font-display text-2xl font-semibold uppercase leading-snug tracking-wide text-[#C6CAD2] sm:text-3xl">
            Level smarter.
          </p>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#9EA4B0] sm:text-lg">
            {release.description.split("Level smarter. ")[1]}
          </p>
        </Reveal>
      </header>

      <section data-testid="addon-download" className="mx-auto max-w-[1400px] px-6 pt-14 lg:px-10">
        <Reveal>
          <div className="relative overflow-hidden border border-[#E52E2E]/40 bg-[#0D0F12] p-8 sm:p-10">
            <EmberCanvas density={8} className="pointer-events-none absolute inset-0 h-full w-full opacity-25" />
            <div className="relative flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="font-mono text-[11px] tracking-[0.35em] text-[#E52E2E]">{release.distribution}</p>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-[#9EA4B0]">
                  Free community beta build. A redistribution review covering third-party quest data
                  is still in progress — full attribution ships inside the package.
                </p>
                <div className="mt-4 flex flex-wrap gap-2 font-mono text-[10px] tracking-[0.2em] text-[#9EA4B0]">
                  <span className="border border-[#282D37] px-2.5 py-1">VERSION v{release.version}</span>
                  <span className="border border-[#282D37] px-2.5 py-1">{release.client.toUpperCase()}</span>
                  <span className="border border-[#282D37] px-2.5 py-1">{release.fileType}</span>
                  <span className="border border-[#282D37] px-2.5 py-1">{release.fileSize}</span>
                </div>
              </div>
              {release.downloadEnabled ? (
                <a
                  href={release.downloadAsset}
                  download
                  data-testid="addon-download-button"
                  className="group flex shrink-0 items-center gap-3 bg-[#E52E2E] px-10 py-5 font-mono text-sm font-bold uppercase tracking-[0.2em] text-white shadow-[0_0_28px_rgba(229,46,46,0.3)] transition-colors duration-300 hover:bg-[#FF3B3B]"
                >
                  <Download size={18} className="transition-transform duration-300 group-hover:translate-y-0.5" />
                  Download {release.name}
                </a>
              ) : (
                <span className="shrink-0 border border-[#282D37] px-10 py-5 font-mono text-sm font-bold uppercase tracking-[0.2em] text-[#5F6573]">
                  Download Locked
                </span>
              )}
            </div>
          </div>
        </Reveal>
      </section>

      <section data-testid="addon-features" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">FEATURES</p>
          <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
            Built like it belongs in the HUD
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {release.features.map((f, i) => (
            <Reveal key={f.title} delay={0.06 * i}>
              <div className="group steel-panel h-full p-7 transition-[border-color] duration-500 hover:border-[#E52E2E]/50">
                <p className="font-mono text-sm tracking-[0.3em] text-[#5F6573] transition-colors duration-500 group-hover:text-[#E52E2E]">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-4 font-display text-xl font-bold uppercase tracking-wide text-[#F4F5F7]">
                  {f.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[#9EA4B0]">{f.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {release.video && (
        <section data-testid="addon-video" className="border-t border-[#282D37]/70 bg-[#0D0F12]">
          <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-6 py-24 lg:grid-cols-[1fr_1.6fr] lg:gap-16 lg:px-10">
            <Reveal>
              <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">GAMEPLAY // IN-GAME FOOTAGE</p>
              <h2 className="mt-4 font-display text-4xl font-black uppercase leading-tight tracking-tight text-[#F4F5F7] sm:text-5xl">
                Watch it work
              </h2>
              <p className="mt-6 text-sm leading-relaxed text-[#9EA4B0] sm:text-base">
                Real v{release.version} footage from WoW Forever — the welcome screen, routes,
                options and the guide HUD doing its job in the open world.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="border border-[#282D37] bg-[#0A0B0D]">
                <div className="flex items-center justify-between border-b border-[#282D37] px-4 py-2.5">
                  <div className="flex items-center gap-2.5">
                    <span className="h-2 w-2 animate-ember-pulse rounded-full bg-[#E52E2E]" />
                    <span className="font-mono text-[10px] tracking-[0.25em] text-[#9EA4B0]">
                      REC · {release.name.toUpperCase()}_V{release.version}
                    </span>
                  </div>
                  <span className="hidden font-mono text-[10px] tracking-[0.2em] text-[#5F6573] sm:block">IN-GAME CAPTURE</span>
                </div>
                <video
                  data-testid="addon-gameplay-video"
                  src={release.video}
                  className="aspect-video w-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  preload="metadata"
                />
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <section data-testid="addon-screenshots" className="border-t border-[#282D37]/70 bg-[#0D0F12]">
        <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">SCREENSHOTS // IN-GAME</p>
            <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
              Inside WoW Forever
            </h2>
          </Reveal>
          {release.screenshots.length === 0 ? (
            <Reveal delay={0.1}>
              <div className="mt-12 border border-dashed border-[#282D37] bg-[#0A0B0D] p-16 text-center">
                <p className="font-mono text-xs tracking-[0.3em] text-[#5F6573]">
                  APPROVED v{release.version} HUD CAPTURES — INBOUND
                </p>
                <p className="mt-3 text-sm text-[#5F6573]">
                  In-game screenshots of the {release.name} interface will land here.
                </p>
              </div>
            </Reveal>
          ) : (
            <div className="mt-12 grid gap-6 sm:grid-cols-2">
              {release.screenshots.map((s, i) => (
                <Reveal key={s.src} delay={0.08 * i}>
                  <button
                    data-testid={`addon-screenshot-${i}`}
                    onClick={() => setLightbox(i)}
                    className="group relative block w-full cursor-zoom-in overflow-hidden border border-[#282D37]"
                  >
                    <img
                      src={s.src}
                      alt={s.caption}
                      className="aspect-video w-full object-cover object-top opacity-90 transition-[transform,opacity] duration-700 group-hover:scale-[1.03] group-hover:opacity-100"
                    />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0A0B0D]/90 to-transparent p-5 text-left">
                      <p className="font-mono text-[10px] tracking-[0.3em] text-[#9EA4B0]">{s.caption.toUpperCase()}</p>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            data-testid="addon-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[90] flex cursor-zoom-out items-center justify-center bg-[#0A0B0D]/95 p-6 backdrop-blur-md"
          >
            <button
              data-testid="addon-lightbox-close"
              onClick={() => setLightbox(null)}
              className="absolute right-6 top-6 text-[#9EA4B0] transition-colors hover:text-[#E52E2E]"
              aria-label="Close"
            >
              <X size={28} />
            </button>
            <motion.figure
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-6xl"
            >
              <img
                src={release.screenshots[lightbox].src}
                alt={release.screenshots[lightbox].caption}
                className="max-h-[80vh] w-full border border-[#282D37] object-contain"
              />
              <figcaption className="mt-4 text-center font-mono text-xs tracking-[0.3em] text-[#9EA4B0]">
                {release.screenshots[lightbox].caption.toUpperCase()}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>

      <section data-testid="addon-install" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">INSTALLATION</p>
            <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
              Ninety seconds to forged
            </h2>
            <ol className="mt-10 space-y-4">
              {release.installSteps.map((step, i) => (
                <li key={i} data-testid={`install-step-${i + 1}`} className="flex gap-4 border border-[#282D37] bg-[#121418] p-5">
                  <span className="font-mono text-sm font-bold text-[#E52E2E]">{String(i + 1).padStart(2, "0")}</span>
                  <div className="flex-1">
                    <p className="text-sm leading-relaxed text-[#9EA4B0]">{step.text}</p>
                    {step.copy && (
                      <div className="mt-3 flex items-center justify-between gap-3 border border-[#282D37] bg-[#0A0B0D] px-4 py-2.5">
                        <code className="break-all font-mono text-xs text-[#F4F5F7]">{step.copy}</code>
                        <CopyButton text={step.copy} testid={`copy-install-${i + 1}`} />
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </Reveal>

          <Reveal delay={0.12}>
            <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">COMMANDS</p>
            <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
              Slash & go
            </h2>
            <div className="mt-10 space-y-px">
              {release.commands.map((c, i) => (
                <div
                  key={c.cmd}
                  data-testid={`command-${i}`}
                  className="flex items-center justify-between gap-4 border border-[#282D37] bg-[#121418] px-5 py-4 transition-[border-color] duration-300 hover:border-[#E52E2E]/40"
                >
                  <div className="flex items-center gap-4">
                    <Terminal size={14} className="shrink-0 text-[#E52E2E]" />
                    <code className="font-mono text-sm font-bold text-[#F4F5F7]">{c.cmd}</code>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="hidden text-right text-xs text-[#9EA4B0] sm:block">{c.desc}</span>
                    <CopyButton text={c.cmd} testid={`copy-command-${i}`} />
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-6 font-mono text-[11px] leading-relaxed tracking-[0.15em] text-[#5F6573]">
              IF WOW REPORTS THE ADDON AS OUT OF DATE, ENABLE "LOAD OUT OF DATE ADDONS".
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="addon-meta" className="border-t border-[#282D37]/70 bg-[#0D0F12]">
        <div className="mx-auto max-w-[1400px] px-6 py-20 lg:px-10">
          <div className="grid gap-10 lg:grid-cols-2">
            <Reveal>
              <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">RELEASE NOTES</p>
              <p className="mt-5 text-sm leading-relaxed text-[#9EA4B0]">{release.changelogSummary}</p>
              <p className="mt-4 font-mono text-[11px] tracking-[0.2em] text-[#5F6573]">
                FULL CHANGELOG & COMPATIBILITY POLICY INCLUDED IN THE PACKAGE.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">LICENSE & ATTRIBUTION</p>
              <p className="mt-5 text-sm leading-relaxed text-[#9EA4B0]">{release.attribution}</p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="mt-12 flex flex-wrap gap-2">
              {release.tags.map((t) => (
                <span key={t} className="border border-[#282D37] px-3 py-1.5 font-mono text-[10px] tracking-[0.25em] text-[#5F6573]">
                  {t}
                </span>
              ))}
              <span className="border border-[#282D37] px-3 py-1.5 font-mono text-[10px] tracking-[0.25em] text-[#5F6573]">
                {release.compatibility.toUpperCase()}
              </span>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default AddonDetail;
